

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  useTheme,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  ApplicationType,
  entity_id,
  getGeolocation,
  Platform,
} from "../Utils/Const";
import useIPAddress from "../Utils/useIPAddress";
import { baseapi } from "../Environmentalurl";
import { useSelector, useDispatch } from "react-redux";
import { setLoginMeta } from "../Slice/authSlice";
import { saveState } from "../Utils/sessionUtility";
import api from "../Utils/Api";
import { setToken } from "../Utils/tokenService";
import { useMediaQuery } from "@mui/system";

// ── Helpers ───────────────────────────────────────────────────────────────────
const maskMobile = (num) => {
  if (!num || num.length < 6) return num;
  return num.slice(0, -4).replace(/./g, "X") + num.slice(-4);
};

const primaryBtnSx = (disabled) => ({
  mt: 2,
  py: 1.4,
  borderRadius: 2,
  fontWeight: 700,
  fontSize: 15,
  textTransform: "none",
  background: disabled
    ? "#e5e7eb"
    : "linear-gradient(135deg, #1a237e, #1565c0)",
  color: disabled ? "#9ca3af" : "#fff",
  boxShadow: disabled ? "none" : "0 4px 16px rgba(21,101,192,0.35)",
  "&:hover": {
    background: disabled
      ? "#e5e7eb"
      : "linear-gradient(135deg, #0d47a1, #1a237e)",
  },
});

const toastError = {
  background: "red",
  color: "white",
  fontWeight: "bold",
  borderRadius: "8px",
};

export default function MerchantOtpPage() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const ip       = useIPAddress();

  // ── Mobile + messageRefId from Redux (set by Loginpage after OTP send) ────
  const loginMeta   = useSelector((state) => state.auth.loginMeta);
  const { messageRefId, mobileNumber } = loginMeta ?? {};

  // ── OTP state ─────────────────────────────────────────────────────────────
  const [otp, setOtp]             = useState(["", "", "", "", "", ""]);
  const [timer, setTimer]         = useState(60);
  const [showResend, setShowResend] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [geo, setGeo]             = useState("");

  // ── Geo on mount ──────────────────────────────────────────────────────────
  useEffect(() => {
    getGeolocation().then((g) => { if (g) setGeo(g); });
  }, []);

  // ── Timer ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (timer <= 0) { setShowResend(true); return; }
    const t = setTimeout(() => setTimer((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [timer]);

  // ── OTP field handlers ────────────────────────────────────────────────────
  const handleChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      document.getElementById(`motp-${index + 1}`)?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`motp-${index - 1}`)?.focus();
    }
  };

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResendOTP = async () => {
    try {
      const res = await api.post(`${baseapi}/auth/walletexistance`, {
        ReqType:     "One_userlogin",
        MobileNumber: mobileNumber,
        EntityID:    entity_id,
        SSOID:       "-",
        MemberID:    "26384597149",
        Statement:   "LoginWithOTP",
        MachineID:   ip,
        Platform,
        Geolocation: geo,
      });

      if (res.data.Response_Code === "1110") {
        dispatch(setLoginMeta({
          isLoggedIn:   true,
          messageRefId: res.data.MessageRefID,
          mobileNumber,
        }));
        setOtp(["", "", "", "", "", ""]);
        setTimer(60);
        setShowResend(false);
        toast.success("OTP resent successfully!", { position: "top-center" });
        document.getElementById("motp-0")?.focus();
      } else {
        toast.error(res.data.Message || "Failed to resend OTP.", {
          style: toastError, position: "top-center",
        });
      }
    } catch {
      toast.error("Failed to resend OTP. Please try again.", {
        style: toastError, position: "top-center",
      });
    }
  };

  // ── Verify OTP → 5555 → store session → merchant dashboard ───────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    if (verifying) return;

    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) {
      toast.error("Please enter the 6-digit OTP.", {
        style: toastError, position: "top-center",
      });
      return;
    }
    if (!messageRefId || !mobileNumber) {
      toast.error("Session expired. Please go back and login again.", {
        style: toastError, position: "top-center",
      });
      return;
    }

    setVerifying(true);
    try {
      const response = await api.post(
        `${baseapi}/auth/loginvalidateotpV2`,
        {
          EntityID:          entity_id,
          ApplicationType,
          Platform,
          ReqType:"One_loginvalidotpV2",
          InputMessageRefID: messageRefId,
          OTP:               enteredOtp,
          Statement:         "LoginValidOTP",
          MachineID:         ip,
          DeviceID:
            "d8StqY8kSK65YINoATrGvH:APA91bG7sxFgCsnOqubyCAzm_H5mnI64uDXFOJ0AH78IMWSEJkZC7PmTTZkh4zG9nBnI0icROb35jKDgD7tRzBN38PVWZhzpmeav_K6K31dy_jdlprtbaktd",
        },
      );

      const data = response.data;

      // Store the JWT token for subsequent API calls
      setToken(data.token);

      if (data.Response_Code === "5555") {
        // ── Merchant login success ─────────────────────────────────────────
        // 1. Store WalletToken (used as Bearer token for merchant APIs)
        if (data.WalletToken) {
          localStorage.setItem("SessionWalletToken", data.WalletToken);
          sessionStorage.setItem("MerchantToken", data.WalletToken);
        }

        // 2. Store MobileNumber
        sessionStorage.setItem("mobileNumber", mobileNumber);

        // 3. Store NameOnCard + SessionID from response
        //    Same pattern as normal user handleLoginSuccess
        try {
          const customerProfileString = data.Info?.[0]?.CustomerProfile;
          if (customerProfileString) {
            const parsed  = JSON.parse(customerProfileString);
            const profile = parsed[0];
            if (profile?.NameOnCard) {
              saveState("NameOnCard", profile.NameOnCard);
              saveState("SessionID",  profile.SessionID);
              // Store SessionID separately for easy access
              sessionStorage.setItem("SessionID", JSON.stringify(profile.SessionID));
            }
          }
        } catch (err) {
          console.error("Merchant profile parse error:", err);
        }

        // 4. Store merchant-specific data from response
        if (data.MerchantID) {
          sessionStorage.setItem("MerchantID", String(data.MerchantID));
        }
        if (data.MerchantName) {
          sessionStorage.setItem("MerchantName", data.MerchantName);
        }

        // 5. Mark as merchant session
        sessionStorage.setItem("isMerchant", "true");

        // 6. Navigate to merchant dashboard
        navigate("/merchant-dashboard", { replace: true });

      } else {
        // Any code other than 5555 is failure for merchant
        toast.error(data.Message || "OTP verification failed. Please try again.", {
          style: toastError,
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: true,
        });
        setOtp(["", "", "", "", "", ""]);
        document.getElementById("motp-0")?.focus();
      }
    } catch {
      toast.error("Error while verifying OTP. Please try again.", {
        style: toastError,
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
      });
    } finally {
      setVerifying(false);
    }
  };

  const isDisabled = verifying || otp.join("").length !== 6;

  // ── UI ─────────────────────────────────────────────────────────────────────
  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnHover={false}
        draggable
        theme="colored"
      />

      <Grid container height="100vh">
        {/* Left brand panel — same style as OtpPage */}
        {!isMobile && (
          <Grid
            item
            xs={false}
            md={6}
            sx={{
              background: "linear-gradient(160deg, #0F172A 0%, #1a237e 50%, #1565c0 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: 6,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative circles */}
            <Box sx={{ position: "absolute", top: -60, right: -60, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
            <Box sx={{ position: "absolute", bottom: -40, left: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />

            {/* Merchant icon */}
            <Box
              sx={{
                width: 80, height: 80, borderRadius: "24px",
                background: "rgba(255,255,255,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 40, mb: 3,
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              🏪
            </Box>
            <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: 28, mb: 1, textAlign: "center" }}>
              TA Merchant Portal
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 15, textAlign: "center", lineHeight: 1.7 }}>
              Manage your transactions,{"\n"}
              settlements, and business analytics{"\n"}
              all in one place.
            </Typography>

            {/* Badges */}
            <Box display="flex" gap={1.5} mt={4} flexWrap="wrap" justifyContent="center">
              {["🔒 Secure", "📊 Analytics", "💳 Settlements"].map((b) => (
                <Box key={b} sx={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "10px", px: 2, py: 0.8 }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 12, fontWeight: 600 }}>{b}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        )}

        {/* Right — OTP form */}
        <Grid
          item
          xs={12}
          md={6}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="white"
          sx={{ p: { xs: 3, md: 3 }, px: { xs: 0, md: 4 } }}
        >
          <Box
            component="form"
            onSubmit={handleVerify}
            sx={{
              width: { xs: "90%", sm: "420px" },
              background: "#fff",
              borderRadius: 4,
              p: 4,
              boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
            }}
          >
            {/* Logo / header */}
            <Box mb={2.5}>
              <Box
                sx={{
                  width: 48, height: 48, borderRadius: "14px",
                  background: "linear-gradient(135deg, #1a237e, #1565c0)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 24, mb: 1.5,
                }}
              >
                🏪
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: 1, mb: 0.5 }}>
                MERCHANT VERIFICATION
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                Enter verification code
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                OTP sent to <b>{maskMobile(mobileNumber)}</b>{" "}
                <Link to="/login" style={{ color: "#1565c0" }}>Edit</Link>
              </Typography>
            </Box>

            {/* OTP boxes */}
            <Box display="flex" gap={1} mb={1}>
              {otp.map((digit, i) => (
                <TextField
                  key={i}
                  id={`motp-${i}`}
                  type="text"
                  value={digit}
                  onChange={(e) => handleChange(e, i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                    style: {
                      textAlign: "center",
                      fontSize: isMobile ? 18 : 22,
                      fontWeight: 700,
                      padding: isMobile ? "10px 0" : "12px 0",
                    },
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      "&.Mui-focused fieldset": {
                        borderColor: "#1565c0",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              ))}
            </Box>

            {/* Verify button */}
            <Button
              type="submit"
              fullWidth
              disabled={isDisabled}
              sx={primaryBtnSx(isDisabled)}
            >
              {verifying ? "Verifying..." : "Verify OTP"}
            </Button>

            {/* Resend / Timer */}
            <Box textAlign="left" mt={1.5}>
              {!showResend ? (
                <Typography fontSize={13} color="text.secondary">
                  Resend OTP in <b>{timer}s</b>
                </Typography>
              ) : (
                <Typography
                  fontSize={14}
                  sx={{
                    color: "#1565c0",
                    cursor: "pointer",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                  onClick={handleResendOTP}
                >
                  Resend OTP
                </Typography>
              )}
            </Box>

            <Box sx={{ height: "1px", background: "#e0e0e0", my: 2 }} />

            <Box textAlign="center">
              <Link to="/login" style={{ color: "#6b7280", fontSize: 13 }}>
                ← Back to Login
              </Link>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}